import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import InGoMap from '../components/maps/InGoMap';
import { useInGoRoad } from '../components/maps/useInGoRoad';
import { useOwnVehicle } from '../components/maps/useOwnVehicle';
import PackagePhotoCapture from '../components/PackagePhotoCapture';
import ClientParcelPhoto from '../components/driver/ClientParcelPhoto';
import DriverJobState from '../components/driver/DriverJobState';
import { jobPath, useDriverJob } from '../components/driver/useDriverJob';
import { useLiveLocation } from '../hooks/useLiveLocation';
import { isPackagePhotoSrc } from '../lib/packagePhoto';
import { formatGBP } from '../lib/currency';
import { isWalletCustomerPayment, isCashCustomerPayment, markDriverLeftAcceptedDelivery } from '../lib/driverIncomingBookings';
import './driverDelivery.css';

function Back() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 19.5L8 12l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
/** Filled handset — standard orientation (no extra rotate; reads clearly in a circle). */
function IcCall() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden className="da-cIco">
      <path
        fill="currentColor"
        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.21z"
      />
    </svg>
  );
}
/** Solid message bubble — flat body + tail bottom-left; inset so it does not clip the circle. */
function IcChat() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden className="da-cIco">
      <path
        fill="currentColor"
        d="M5.5 4.5h13V10.5H9.6L6.4 14.2V12H5.5V4.5z"
      />
    </svg>
  );
}

const fmt$ = (n) => formatGBP(n);

export default function DriverActiveDeliveryPage() {
  const navigate = useNavigate();
  const live = useLiveLocation({ mapThrottleMs: 8000 });
  // The rider on their own vehicle, blue: on a delivery.
  const { vehicle: ownKind, position: ownPosition } = useOwnVehicle(live);
  const ownVehicle = useMemo(() => ({ vehicle: ownKind, state: 'delivering' }), [ownKind]);
  const { order, status: jobStatus, error: jobError, reload: reloadJob } = useDriverJob();
  // Every hook below must still run while the job resolves, so `o` is an empty
  // object rather than a fixture. The screen itself is gated at the return.
  const o = useMemo(() => order || {}, [order]);
  // InGo's job carries the pickup's contact and its two pins: nothing to fetch
  // or geocode.
  const customerName = o.customerName;
  const customerPhone = o.customerPhone;
  const pickupGeo = o.pickupPoint || null;
  const dropoffGeo = o.dropoffPoint || null;
  const [driverPhotoSrc, setDriverPhotoSrc] = useState(() =>
    isPackagePhotoSrc(o?.driverPackagePhotoDataUrl) ? o.driverPackagePhotoDataUrl : null,
  );

  const pickup = String(o.from || '').trim();
  const dropoff = String(o.to || '').trim() || pickup;

  useEffect(() => {
    if (isPackagePhotoSrc(o.driverPackagePhotoDataUrl)) setDriverPhotoSrc(o.driverPackagePhotoDataUrl);
  }, [o.driverPackagePhotoDataUrl]);

  const startDeliveryRouteNav = () => {
    navigate(jobPath('navigation', o), {
      state: {
        order: {
          ...o,
          driverPackagePhotoDataUrl: driverPhotoSrc || o.driverPackagePhotoDataUrl,
        },
        pickup,
        dropoff,
        dest: pickup,
        navLeg: 'toPickup',
      },
    });
  };

  // An InGo job draws on InGo's map and road (the mobile plan's M8).
  const apiRoad = useInGoRoad(pickupGeo, dropoffGeo);

  if (jobStatus !== 'ready') {
    return <DriverJobState status={jobStatus} error={jobError} label="delivery" onRetry={reloadJob} />;
  }

  return (
    <div className="da-page dd" role="main" aria-label="Active delivery">
      <header className="da-h">
        <button
          type="button"
          className="da-back"
          onClick={() => {
            if (o.bookingTable && o.deliveryId) {
              markDriverLeftAcceptedDelivery(o.bookingTable, o.deliveryId);
            }
            navigate('/driver/home');
          }}
          aria-label="Back"
        >
          <Back />
        </button>
        <div className="da-ht">
          <h1>Active Delivery</h1>
          <p className="da-oid">{o.id}</p>
        </div>
      </header>

      <div className="da-map da-map--gmap">
        {pickupGeo && dropoffGeo ? (
          <InGoMap
            pins={[{ role: 'pickup', ...pickupGeo }, { role: 'dropoff', ...dropoffGeo }]}
            route={apiRoad}
            rider={ownPosition}
            riderVehicle={ownVehicle}
            label="Pickup to drop-off route"
            height="100%"
            bleed
          />
        ) : null}
      </div>

      <div className="da-sheet">
        <div className="da-sc">
          <div className="da-card">
            <p className="da-ps">Head to Pickup</p>
            <div className="da-journey" aria-label="Journey route">
              <div className="da-jrow">
                <span className="da-jdot da-jdot--pickup" aria-hidden />
                <div>
                  <p className="da-jlab">Pickup</p>
                  <p className="da-jaddr">{pickup}</p>
                </div>
              </div>
              <div className="da-jsep" aria-hidden />
              <div className="da-jrow">
                <span className="da-jdot da-jdot--dropoff" aria-hidden />
                <div>
                  <p className="da-jlab">Drop-off</p>
                  <p className="da-jaddr">{dropoff}</p>
                </div>
              </div>
            </div>
            <p className="da-eta">
              {o.dist}
              {' '}
              ·
              {o.eta}
              {' '}
              away
            </p>
            <button type="button" className="da-navB" onClick={startDeliveryRouteNav}>
              Start delivery route
            </button>
          </div>

          <div className="da-cu">
            <div className="da-av" aria-hidden />
            <div className="da-cB">
              <p className="da-cn">{customerName || 'Customer'}</p>
              <p className="da-sen">Sender</p>
              {customerPhone ? (
                <p className="da-cphone" style={{ margin: '0.2rem 0 0', fontSize: '0.95rem', fontWeight: 600 }}>
                  {customerPhone}
                </p>
              ) : null}
            </div>
            <div className="da-cAc">
              <a
                className="da-cB2"
                href={customerPhone ? `tel:${String(customerPhone).replace(/\s/g, '')}` : '#'}
                onClick={(e) => {
                  if (!customerPhone) e.preventDefault();
                }}
                aria-label="Call customer"
                title="Call"
                style={!customerPhone ? { pointerEvents: 'none', opacity: 0.45 } : undefined}
              >
                <IcCall />
              </a>
              <Link
                to="/chat"
                state={{ name: customerName, role: 'driver' }}
                className="da-cB2"
                aria-label="Message customer"
              >
                <IcChat />
              </Link>
            </div>
          </div>

          <div className="da-badR" aria-label="Package">
            <span className="da-bdg">
              {o.size || o.pkg}
            </span>
            <span className="da-bdg">
              {o.type || o.pkg}
            </span>
            {o.packageWeight ? (
              <span className="da-bdg" title="Package weight">
                {o.packageWeight}
              </span>
            ) : null}
          </div>
          {o.hasPackagePhoto ? <ClientParcelPhoto deliveryId={o.deliveryId} /> : null}
          {/* The photo goes with the job to the pickup, where InGo keeps it. */}
          <div style={{ margin: '0.35rem 0 0.45rem' }}>
            <PackagePhotoCapture
              compact
              value={driverPhotoSrc}
              onChange={(url) => setDriverPhotoSrc(url)}
              label="Your package photo"
              hint="Take or upload a photo of the parcel at pickup."
            />
          </div>
          {o.customerPayment && o.customerPayment !== '—' ? (
            <div
              className={`da-customerPay${
                isWalletCustomerPayment(o.payment_method) || isWalletCustomerPayment(o.customerPayment) || o.paidOnline
                  ? ' da-customerPay--wallet'
                  : ''
              }${
                isCashCustomerPayment(o.payment_method) || isCashCustomerPayment(o.customerPayment)
                  ? ' da-customerPay--cash'
                  : ''
              }`}
              role="status"
            >
              <p className="da-customerPay__line">
                Payment:{' '}
                <strong>
                  {isWalletCustomerPayment(o.payment_method) || isWalletCustomerPayment(o.customerPayment)
                    ? 'Wallet Payment'
                    : isCashCustomerPayment(o.payment_method) || isCashCustomerPayment(o.customerPayment)
                      ? 'Cash Payment'
                      : o.customerPayment}
                </strong>
              </p>
              {isWalletCustomerPayment(o.payment_method) || isWalletCustomerPayment(o.customerPayment) ? (
                <p className="da-customerPay__note">
                  Customer has already paid through the Ingo Wallet. Do not collect cash.
                </p>
              ) : null}
              {o.paidOnline ? (
                <p className="da-customerPay__note">The client has already paid online. Do not collect cash.</p>
              ) : null}
              {isCashCustomerPayment(o.payment_method) || isCashCustomerPayment(o.customerPayment) ? (
                <p className="da-customerPay__note da-customerPay__note--cash">
                  Collect cash from the customer at drop-off.
                </p>
              ) : null}
            </div>
          ) : null}
          {o.specialInstructions ? <p className="da-misc">{o.specialInstructions}</p> : null}

          <div className="da-rowE">
            <span className="da-eL">You will earn</span>
            <span className="da-eR">{fmt$(o.amount)}</span>
          </div>

          <div className="da-acts" role="group" aria-label="Next actions">
            <button
              type="button"
              className="da-ia"
              onClick={() =>
                navigate(jobPath('confirm-pickup', o), {
                  state: {
                    order: {
                      ...o,
                      driverPackagePhotoDataUrl: driverPhotoSrc || o.driverPackagePhotoDataUrl,
                    },
                  },
                })
              }
            >
              I Have Arrived
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
